import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Tables } from "@/integrations/supabase/types";

interface HeroBlockProps {
  article: Tables<"case_articles">;
}

function splitTitle(name: string) {
  const separators = [" — ", " – ", " - "];
  for (const sep of separators) {
    const idx = name.indexOf(sep);
    if (idx > 0) {
      return {
        main: name.slice(0, idx),
        sub: name.slice(idx + sep.length),
      };
    }
  }
  return { main: name, sub: null };
}

export function HeroBlock({ article }: HeroBlockProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 0.8], [0.25, 0.65]);

  const { main, sub } = splitTitle(article.project_name);

  const metaTags = [
    article.client_name && { label: "Designer", value: article.client_name },
    article.location && { label: "Location", value: article.location },
    article.area && { label: "Area", value: article.area },
  ].filter(Boolean) as { label: string; value: string }[];

  return (
    <section ref={ref} className="relative w-full overflow-hidden">
      {article.hero_image_url && (
        <div className="relative min-h-[75vh] md:min-h-[90vh] w-full overflow-hidden">
          <motion.img
            src={article.hero_image_url}
            alt={article.project_name}
            className="absolute inset-0 h-[125%] w-full object-cover"
            style={{ y: imgY }}
          />
          <motion.div
            className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent"
            style={{ opacity: overlayOpacity }}
          />

          {/* Content */}
          <div className="relative z-10 flex flex-col justify-end h-full min-h-[75vh] md:min-h-[90vh] px-6 md:px-16 lg:px-24 pb-12 md:pb-20">
            <div className="max-w-[1100px]">
              {/* Main title */}
              <motion.h1
                className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white tracking-[0.02em] leading-[1.1] mb-3"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              >
                {main}
              </motion.h1>

              {/* Subtitle */}
              {sub && (
                <motion.p
                  className="font-serif text-lg sm:text-xl md:text-2xl text-white/60 italic tracking-[0.01em] leading-relaxed mb-8 max-w-[800px]"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
                >
                  {sub}
                </motion.p>
              )}

              {/* Minimal metadata line */}
              {metaTags.length > 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.55 }}
                >
                  <div className="w-12 h-px bg-white/30 mb-5" />
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    {metaTags.map((tag, i) => (
                      <span key={i} className="font-sans text-[11px] md:text-xs uppercase tracking-[0.2em] text-white/50">
                        {tag.value}
                      </span>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fallback: no hero image */}
      {!article.hero_image_url && (
        <div className="mx-auto max-w-[1100px] px-6 py-16 md:py-24">
          <h1 className="font-serif text-3xl md:text-5xl lg:text-6xl text-foreground tracking-[0.02em] leading-[1.1] mb-3">
            {main}
          </h1>
          {sub && (
            <p className="font-serif text-lg md:text-2xl text-muted-foreground italic leading-relaxed">
              {sub}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
